import angular from "angular";

import {
  ApiListItem,
  Calculation,
  Calculations,
  Employments,
  ODataQueryOptions,
  ODataResult,
  SharingUriType,
  Taxcards,
  Translations
} from "@salaxy/core";

import { CalculationCrudController } from "../calc/CalculationCrudController";

import {
  InvoicesService, OnboardingService, ReportsService, SessionService, UiHelpers,
} from "../../services";

/**
 * Controller for sharing the calculation between worker and employer.
 */
export class CalcSharingController extends CalculationCrudController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Calculations", "ReportsService", "UiHelpers", "$location", "$routeParams", "SessionService", "InvoicesService", "Employments", "OnboardingService", "Taxcards"];

  /** Employing relations for the current account */
  public employingRelations: ApiListItem[] = [];

  /** True, if the current link has been copied to the clipboard */
  public uriCopied = false;

  /** If true, template shows option to share direct link to the calculation. Currently used only in Rakennusliitto site. */
  public showLinkSharing: boolean;

  /** Web sharing targets */
  public webSharingTargets = {
    email: {
      title: Translations.get("SALAXY.NG1.CalcSharingComponent.sharingTargets.email"),
      icon: "fa-envelope-o",
      action: () => this.showEmailSharing(),
    },
    whatsApp: {
      title: "WhatsApp",
      icon: "fa-whatsapp",
      getUrl: () => this.getWhatsAppUrl(),
    },
    telegram: {
      title: "Telegram",
      icon: "fa-telegram",
      getUrl: () => this.getTelegramUrl(),
    },
  };

  /** Web sharing targets */
  public mobileSharingTargets = {
    email: {
      title: Translations.get("SALAXY.NG1.CalcSharingComponent.sharingTargets.email"),
      icon: "fa-envelope-o",
      action: () => this.showEmailSharing(),

    },
    device: {
      title: Translations.get("SALAXY.NG1.CalcSharingComponent.sharingTargets.device"),
      icon: "fa-share",
      action: () => this.shareInMobile(),
    },
  };

  /** Loading indicator. */
  public isSharingType: string = null;

  /**
   * Creates a new CalcSharingController
   * @ignore
   */
  constructor(
    private sharingCalculationsApi: Calculations,
    reportsService: ReportsService,
    uiHelpers: UiHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    sessionService: SessionService,
    invoicesService: InvoicesService,
    private employmentsApi: Employments,
    private onboardingService: OnboardingService,
    private taxcards: Taxcards,
  ) {
    super(sharingCalculationsApi, reportsService, uiHelpers, $location, $routeParams, sessionService, invoicesService);
  }

  /**
   * Implement IController
   */
  public $onInit() {
    super.$onInit();
    this.loadEmployingRelations();
  }

  /** Show sharing page for the current calculation. */
  public showSharing(action: string = null): Promise<angular.ILocationService> {
    const savePending = (): Promise<Calculation> => {
      if ((!action || action === "default")) {
        if (!this.current.isReadOnly) {
          return this.save();
        }
      }
      return Promise.resolve(this.current);
    };
    return savePending().then((calculation: Calculation) => {
      return this.$location.url(`/calc/sharing/${calculation.id}${action ? "/" + action : ""}`);
    });
  }

  /**
   * Shares the current calculation.
   * @param type Type of the sharing action or link.
   * @param employer List item for employer to share the calculation with. Mandatory in Direct share.
   * @returns Sharing object of the calculation.
   */
  public share(type: SharingUriType = SharingUriType.Undefined, employer: ApiListItem = null): Promise<angular.ILocationService> {

    if (type !== SharingUriType.Undefined) {
      if (!this.sessionService.checkAccountVerification()) {
        this.showMissingSignatureAlert();
        return Promise.resolve(null);
      }
    }

    const createSharingMessageForEmployer = (): Promise<{ to: string, cc: string, message: string }> => {
      if (type !== SharingUriType.Employer) {
        return Promise.resolve(null);
      }
      return this.createSharingMessage(employer);
    };

    this.isSharingType = `${type}${employer?.ownerId ? employer.ownerId : ""}`;
    return createSharingMessageForEmployer().then( (item) => {
      if (type === SharingUriType.Employer) {
        if (!item) {
          this.isSharingType = null;
          return null;
        }
      }
      return this.sharingCalculationsApi.share(this.currentId, type, employer?.ownerId, item?.to, item?.cc, item?.message).then(() => {
        if (type === SharingUriType.Employer) {
          this.uiHelpers.showAlert(Translations.get("SALAXY.NG1.CalcSharingComponent.emailSent")+ ".");
        }
        return this.reload().then(() => {
          this.isSharingType = null;
          return this.showDetails(this.current);
        });
      });
    });
  }

  /**
   * Loads employing relations of the existing employers for the current account.
   * @param options OData query options.
   */
  public loadEmployingRelations(options: ODataQueryOptions = null): Promise<ApiListItem[]> {
    return this.employmentsApi.getODataForEmployingRelations(options).then((result: ODataResult<ApiListItem>) => {

      // filter out duplicates, default sort is updatedAt desc
      const distinctItems: ApiListItem[] = [];
      result.items.forEach((x) => {
        if (distinctItems.findIndex((d) => d.ownerId === x.ownerId) === -1) {
          distinctItems.push(x);
        }
      });
      this.employingRelations = distinctItems;
      return this.employingRelations;
    });
  }

  /**
   * Returns validation for current calculation or empty validation object if the calculation has not been recalculated (validated).
   */
  public get validation() {
    return this.current?.result?.validation || { errors: [], hasAllRequiredFields: true, isValid: true };
  }

  /** Returns the employer for the given id using employing employers as a source */
  public get employer(): any {
    if (this.current && this.current.sharing.type === SharingUriType.Employer && this.employingRelations) {
      return this.employingRelations.find((x) => x.ownerId === (this.current.sharing.uri));
    }
    return null;
  }

  /** Returns email address for current accoount */
  public get cc(): string {
    if (this.sessionService.session) {
      return this.sessionService.session.currentAccount.contact.email;
    }
    return null;
  }

  /** Copies link to clipboard */
  public copyUri() {
    (navigator as any).clipboard.writeText(this.current.sharing.uri);
    this.uriCopied = true;
  }

  /** Returns true if the given typing is just processing sharing */
  public isSharing(type: string): boolean {
    return type === this.isSharingType;
  }

  /** Returns true if the navigator.share -function exists. */
  public get isMobileSharingEnabled() {
    return !!(navigator as any).share;
  }

  /** Whatsapp-link */
  private getWhatsAppUrl(): string {
    if (!this.current) {
      return "";
    }
    return `https://wa.me/?text=${encodeURIComponent(this.getSharingMessage() + "\n" + this.current.sharing.uri)}`;
  }

  /** Telegram-link */
  private getTelegramUrl(): string {
    if (!this.current) {
      return "";
    }
    return `https://telegram.me/share/url?url=${encodeURIComponent(this.current.sharing.uri)}&text=${encodeURIComponent(this.getSharingMessage())}`;
  }

  /** Mobile sharing */
  private shareInMobile(): Promise<any> {
    const data = {
      url: this.current.sharing.uri,
      title: this.getSharingTitle(),
      text: this.getSharingMessage(),
    };
    return (navigator as any).share(data);
  }

  private showEmailSharing(): void {
    this.createSharingMessage().then( (item) => {
      if (item) {
        this.sendSharingEmail(item.to, item.cc, item.message);
      }
    });
  }

  private createSharingMessage(employer?: ApiListItem ): Promise<{ to: string, cc: string, message: string }> {
    const item = {
      to: employer?.ownerInfo?.email || "",
      cc: this.cc,
      message: "",
    };
    return this.uiHelpers.openEditDialog("salaxy-components/modals/calc/EmailSharing.html", item, null)
      .then((dialog) => {
        if (dialog.result === "ok") {
          return dialog.item;
        } else {
          return null;
        }
      });
  }

  private sendSharingEmail(to: string, cc: string, message: string): Promise<boolean> {
    this.isSharingType = "notification";
    return this.sharingCalculationsApi.notifySharing(this.currentId, to, cc, message).then((result) => {
      this.isSharingType = null;
      if (result === true) {
        this.uiHelpers.showAlert(Translations.get("SALAXY.NG1.CalcSharingComponent.emailSendSuccess")+ ".");
      } else {
        this.uiHelpers.showAlert(Translations.get("SALAXY.NG1.CalcSharingComponent.emailSent")+ ".");
      }
      return this.reload().then( () => {
        return result;
      });
    });
  }

  private getSharingMessage(): string {
    return `${Translations.get("SALAXY.NG1.CalcSharingComponent.sharingMessage.calculationSent")}. \n ${Translations.get("SALAXY.NG1.CalcSharingComponent.sharingMessage.login")}!`;
  }

  private getSharingTitle(): string {
    return Translations.get("SALAXY.NG1.CalcSharingComponent.sharingTitle");
  }

  /**
   * Shows an alert if the signature is missing.
   */
  private showMissingSignatureAlert() {
    this.uiHelpers.showConfirm(Translations.get("SALAXY.NG1.CalcSharingComponent.missingSignatureAlert.missingSignature"),
    `${Translations.get("SALAXY.NG1.CalcSharingComponent.missingSignatureAlert.finishAccountCreation")}\n ${Translations.get("SALAXY.NG1.CalcSharingComponent.missingSignatureAlert.signAuthorization")} \n\n ${Translations.get("SALAXY.NG1.CalcSharingComponent.missingSignatureAlert.continueToSigning")}`,
    Translations.get("SALAXY.NG1.CalcSharingComponent.missingSignatureAlert.continue"), Translations.get("SALAXY.NG1.CalcSharingComponent.missingSignatureAlert.close")).then((ok: boolean) => {
        if (ok) {
          this.onboardingService.launchWorkerOnboarding();
        }
      });
  }
}
